import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getCookie, removeCookie, setCookie } from '@/utils/cookie'
import { getUserInfo, logout } from '@/api/user'

export const TOKEN_KEY = 'TOKEN'
export const DISPLAY_NAME_KEY = 'FINDBAO_USERNAME'

export interface UserInfo {
  userId?: number | string
  username?: string
  userName?: string
  email?: string
  [key: string]: unknown
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>()
  const user = ref<UserInfo>({})
  const persistedDisplayName = ref('')
  const loadingUser = ref(false)
  const isLogin = computed(() => Boolean(token.value))
  const displayName = computed(() => persistedDisplayName.value || resolveDisplayName(user.value))

  function normalizeDisplayName(value: unknown) {
    return typeof value === 'string' ? value.trim().slice(0, 80) : ''
  }

  function resolveDisplayName(value: UserInfo) {
    return (
      [value.username, value.userName, value.email].map(normalizeDisplayName).find(Boolean) || ''
    )
  }

  function persistDisplayName(value: unknown) {
    const nextDisplayName = normalizeDisplayName(value)
    if (!nextDisplayName) return
    persistedDisplayName.value = nextDisplayName
    setCookie(DISPLAY_NAME_KEY, nextDisplayName)
  }

  function init() {
    token.value = getCookie(TOKEN_KEY)
    persistedDisplayName.value = normalizeDisplayName(getCookie(DISPLAY_NAME_KEY))
    if (token.value) {
      void loadUser()
    } else {
      persistedDisplayName.value = ''
      removeCookie(DISPLAY_NAME_KEY)
    }
  }

  function setToken(value: string, identity?: UserInfo) {
    token.value = value
    setCookie(TOKEN_KEY, value)
    if (identity) {
      user.value = identity
      persistDisplayName(resolveDisplayName(identity))
    }
  }

  async function loadUser() {
    if (!token.value || loadingUser.value) return
    loadingUser.value = true
    try {
      const response = await getUserInfo()
      user.value = response.data || {}
      if (!persistedDisplayName.value) persistDisplayName(resolveDisplayName(user.value))
    } finally {
      loadingUser.value = false
    }
  }

  async function signOut() {
    try {
      await logout()
    } catch {
      /* Clear local state even if the session already expired. */
    }
    removeCookie(TOKEN_KEY)
    removeCookie(DISPLAY_NAME_KEY)
    token.value = undefined
    user.value = {}
    persistedDisplayName.value = ''
  }

  return { token, user, loadingUser, isLogin, displayName, init, setToken, loadUser, signOut }
})
